import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, ArrowRight, Mail, Linkedin, Calendar, MapPin } from 'lucide-react';

const LeadershipProfile = () => {
  const { leaderId } = useParams();
  
  const leaders = {
    'keyvin-runtuwarouw': {
      id: 'keyvin-runtuwarouw',
      name: 'Keyvin Runtuwarouw',
      title: 'Chief Executive Officer',
      image: '/assets/Keyvin%20Runtuwarouw%204x3.jpg',
      location: 'Perth, Australia',
      joinDate: 'January 2025',
      email: 'keyvin@vincotek.com',
      linkedin: '#',
      shortBio: 'Visionary leader driving innovation and growth at Vincotek with extensive experience in technology and business strategy.',
      fullBio: [
        "Keyvin Runtuwarouw brings over a decade of experience in technology leadership and business strategy to his role as Chief Executive Officer at Vincotek. His vision for accessible, impactful technology solutions has been the driving force behind the company's founding and mission.",
        "Prior to establishing Vincotek, Keyvin held senior leadership positions at several innovative technology companies, where he specialized in digital transformation, product development, and strategic partnerships. His approach to leadership emphasizes collaboration, innovation, and social responsibility – values that are now deeply embedded in Vincotek's culture.",
        "Keyvin is passionate about leveraging technology to solve real-world problems and believes strongly in the power of business to create positive social impact. Under his leadership, Vincotek has committed to dedicating 2.5% of revenue to humanitarian initiatives, reflecting his personal commitment to giving back to the community.",
        "When he's not working on Vincotek's next breakthrough, Keyvin enjoys mentoring young entrepreneurs, exploring emerging technologies, and spending time with his family. He holds a Master's degree in Business Administration and is a frequent speaker at technology and leadership conferences across Australia."
      ],
      expertise: ['Strategic Leadership', 'Technology Innovation', 'Business Development', 'Digital Transformation', 'Social Impact', 'Team Building'],
      achievements: [
        'Founded Vincotek with a mission-driven approach to technology',
        'Led digital transformation initiatives for Fortune 500 companies',
        'Established humanitarian giving program dedicating 2.5% of revenue',
        'Speaker at major technology and leadership conferences',
        'Mentor for emerging technology entrepreneurs'
      ]
    },
    'Rey-Runtuwarouw': {
      id: 'Rey-Runtuwarouw',
      name: 'Rey Runtuwarouw',
      title: 'Chief Corporate Officer',
      image: '/assets/Margareta%20Tanuwijaya%204x3.jpg',
      location: 'Perth, Australia',
      joinDate: 'January 2025',
      email: 'rey@vincotek.com',
      linkedin: '#',
      shortBio: 'Strategic executive focused on corporate development, partnerships, and organizational excellence.',
      fullBio: [
        "Rey Runtuwarouw serves as Chief Corporate Officer at Vincotek, where she oversees corporate strategy, partnership development, and organizational excellence. Her expertise in building scalable operations and fostering strategic relationships has been instrumental in shaping Vincotek's corporate foundation.",
        "With extensive experience in corporate development and operations management, Rey has a proven track record of building efficient, people-centered organizations that deliver exceptional results. Her strategic approach to partnerships has enabled numerous successful collaborations and business growth initiatives.",
        "Rey is deeply committed to creating inclusive, diverse workplaces where every team member can thrive. She leads Vincotek's efforts in organizational development, ensuring that the company's rapid growth is supported by strong systems, processes, and a positive company culture.",
        "Beyond her corporate responsibilities, Rey is actively involved in professional development programs and women's leadership initiatives in the technology sector. She holds advanced degrees in Business Administration and Organizational Psychology, and regularly contributes to industry publications on topics related to corporate governance and leadership."
      ],
      expertise: ['Corporate Strategy', 'Partnership Development', 'Operations Excellence', 'Team Leadership', 'Organizational Development', 'Change Management'],
      achievements: [
        'Co-founded Vincotek with focus on sustainable growth',
        'Developed strategic partnerships driving business expansion',
        'Implemented organizational excellence programs',
        'Advocate for diversity and inclusion in technology',
        'Published thought leadership on corporate governance'
      ]
    }
  };

  const currentLeader = leaders[leaderId];
  
  if (!currentLeader) {
    return <Navigate to="/about/leadership" replace />;
  }

  const otherLeaders = Object.values(leaders).filter(leader => leader.id !== leaderId);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/about/leadership">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Leadership
          </Button>
        </Link>
      </div>

      {/* Profile Header */}
      <section className="pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Profile Image */}
            <div className="space-y-6">
              <div className="relative">
                <div className="w-full max-w-md mx-auto h-96 rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={currentLeader.image} 
                    alt={currentLeader.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                  <div className="text-vincotek-dark text-sm font-semibold">
                    {currentLeader.title}
                  </div>
                </div>
              </div>
              
              {/* Contact Info */}
              <Card className="bg-gradient-to-br from-gray-50 to-blue-50/30 border-0">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-vincotek-dark mb-4">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 text-gray-600">
                      <Mail className="h-4 w-4" />
                      <a href={`mailto:${currentLeader.email}`} className="hover:text-vincotek-blue transition-colors">
                        {currentLeader.email}
                      </a>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-600">
                      <Linkedin className="h-4 w-4" />
                      <a href={currentLeader.linkedin} className="hover:text-vincotek-blue transition-colors">
                        LinkedIn Profile
                      </a>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-600">
                      <MapPin className="h-4 w-4" />
                      <span>{currentLeader.location}</span>
                    </div>
                    <div className="flex items-center space-x-3 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Joined {currentLeader.joinDate}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Profile Content */}
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark mb-4">
                  {currentLeader.name}
                </h1>
                <p className="text-xl text-vincotek-blue font-semibold mb-6">
                  {currentLeader.title}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {currentLeader.shortBio}
                </p>
              </div>

              {/* Expertise */}
              <div>
                <h3 className="text-2xl font-semibold text-vincotek-dark mb-4">Areas of Expertise</h3>
                <div className="flex flex-wrap gap-3">
                  {currentLeader.expertise.map((skill, index) => (
                    <span
                      key={index}
                      className="bg-gradient-to-r from-vincotek-yellow/20 to-vincotek-blue/20 text-vincotek-dark px-4 py-2 rounded-full text-sm font-medium border border-gray-200"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Key Achievements */}
              <div>
                <h3 className="text-2xl font-semibold text-vincotek-dark mb-4">Key Achievements</h3>
                <ul className="space-y-3">
                  {currentLeader.achievements.map((achievement, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span className="text-gray-600 leading-relaxed">{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Biography */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-8 text-center">
              About {currentLeader.name.split(' ')[0]}
            </h2>
            <div className="space-y-6">
              {currentLeader.fullBio.map((paragraph, index) => (
                <p key={index} className="text-lg text-gray-600 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Other Leadership Profiles */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-4">
              Meet Our Other Leaders
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover more about the exceptional team driving Vincotek's vision forward.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {otherLeaders.map((leader) => (
              <Link key={leader.id} to={`/about/leadership/${leader.id}`}>
                <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer bg-white border-0">
                  <CardContent className="p-8 text-center">
                    <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full mx-auto mb-6 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold text-xl">{leader.name.charAt(0)}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-vincotek-dark mb-2">
                      {leader.name}
                    </h3>
                    <p className="text-vincotek-blue font-medium mb-4">
                      {leader.title}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-6">
                      {leader.shortBio}
                    </p>
                    <Button variant="outline" className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group-hover:shadow-lg">
                      View Profile
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA - Duplicate profile info */}
      <section className="py-16 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-white font-bold text-2xl">{currentLeader.name.charAt(0)}</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">{currentLeader.name}</h2>
            <p className="text-vincotek-yellow font-semibold mb-6">{currentLeader.title}</p>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Interested in connecting with {currentLeader.name.split(' ')[0]} or learning more about Vincotek? 
              We'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  Get in Touch
                </Button>
              </Link>
              <Link to="/about/leadership">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-vincotek-dark px-8 py-3 rounded-full transition-all duration-300">
                  Back to Leadership
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadershipProfile;